import { LitElement, html, nothing, type PropertyValues } from 'lit';
import styles from './styles.js';
import { property, query } from 'lit/decorators.js';
import type { Placement } from '../types.js';
import { emit } from '../utils/dom.utils.js';
import { PlacementController } from '../utils/placement.controller.js';

/**
 * uwc-overlay
 *
 * The foundational positioned overlay. Free-form content via slots;
 * no opinionated header / footer structure (see uwc-popover for that).
 *
 * @slot trigger        - The element that opens the overlay on click.
 *                        Omit when using trigger-id for external triggers.
 * @slot content        - Main overlay content.
 * @slot (default)      - Fallback content slot (external-trigger pattern).
 *
 * @attr trigger-id     - ID of an external trigger element. Supports deep
 *                        shadow-DOM traversal.
 * @attr placement      - Panel placement relative to the trigger.
 *                        One of: top | top-start | top-end | bottom |
 *                        bottom-start | bottom-end | left | left-start |
 *                        left-end | right | right-start | right-end
 * @attr offset         - Gap in px between trigger and panel. Default: 8.
 * @attr backdrop       - Show a semi-transparent backdrop behind the panel.
 * @attr close-on-escape        - Close on Escape key. Default: true.
 * @attr close-on-outside-click - Close on click outside. Default: true.
 *
 * @fires uwc-show      - Fired when the panel opens.
 * @fires uwc-hide      - Fired when the panel closes.
 *
 * @csspart trigger     - The trigger slot wrapper.
 * @csspart panel       - The overlay panel element.
 * @csspart backdrop    - The backdrop element.
 *
 * @cssprop --uwc-overlay-bg           - Panel background. Default: #fff.
 * @cssprop --uwc-overlay-border       - Panel border.
 * @cssprop --uwc-overlay-radius       - Panel border-radius. Default: 10px.
 * @cssprop --uwc-overlay-shadow       - Panel box-shadow.
 * @cssprop --uwc-overlay-z            - Panel z-index. Default: 9999.
 * @cssprop --uwc-overlay-duration     - Open/close transition duration. Default: 160ms.
 * @cssprop --uwc-backdrop-color       - Backdrop background. Default: rgba(0,0,0,0.25).
 */
export class UwcOverlay extends LitElement {

  // ── Public properties ───────────────────────────────────────────

  @property({ type: String, attribute: 'trigger-id'             }) triggerId?: string;
  @property({ type: String                                       }) placement: Placement = 'bottom';
  @property({ type: Number                                       }) offset = 8;
  @property({ type: Boolean, reflect: true                       }) backdrop = false;
  @property({ type: Boolean, attribute: 'close-on-escape'        }) closeOnEscape = true;
  @property({ type: Boolean, attribute: 'close-on-outside-click' }) closeOnOutsideClick = true;

  // ── Internal refs ────────────────────────────────────────────────

  @query('#panel')    private _panel!:    HTMLElement;
  @query('#backdrop') private _backdrop!: HTMLElement;

  // ── Controller ───────────────────────────────────────────────────

  private _pc = new PlacementController(this, {
    getPanelEl: () => this._panel,

    onTriggerFound: (el) => {
      const handler = (e: Event): void => {
        e.stopPropagation();
        this._pc.toggle();
      };
      el.addEventListener('click', handler);
      return () => el.removeEventListener('click', handler);
    },

    get dismissOnOutsideClick() { return true; },  // always wired; actual gate in _handleOutsideClick
    get dismissOnEscape()       { return true; },

    afterShow: () => {
      if (this.backdrop) this._backdrop?.showPopover();
      emit(this, 'uwc-show', { triggeredBy: this._pc.triggerEl ?? undefined });
    },
    afterHide: () => {
      try { this._backdrop?.hidePopover(); } catch {}
      emit(this, 'uwc-hide', { triggeredBy: this._pc.triggerEl ?? undefined });
    },
  });

  // ── Lifecycle ────────────────────────────────────────────────────

  override firstUpdated(): void {
    this._pc.firstUpdated(
      this.triggerId ?? null,
      this.shadowRoot!.querySelector<HTMLSlotElement>('slot[name="trigger"]'),
    );
  }

  protected override updated(changed: PropertyValues): void {
    super.updated(changed);
    this._pc.updated(
      changed as Map<PropertyKey, unknown>,
      this.triggerId ?? null,
      this.shadowRoot!.querySelector<HTMLSlotElement>('slot[name="trigger"]'),
    );
  }

  // ── Public imperative API ────────────────────────────────────────

  show(): void   { this._pc.show(); }
  hide(): void   { this._pc.hide(); }
  toggle(): void { this._pc.toggle(); }
  get isOpen(): boolean { return this._pc.open; }

  // ── Render ───────────────────────────────────────────────────────

  protected override render() {
    const isExternal = Boolean(this.triggerId);
    const open       = this._pc.open;

    return html`
      ${!isExternal
        ? html`<slot name="trigger" part="trigger"></slot>`
        : nothing}

      ${this.backdrop
        ? html`<div id="backdrop" part="backdrop" popover="manual"
                    class="backdrop ${open ? 'is-open' : ''}"
                    @click=${() => { if (this.closeOnOutsideClick) this._pc.hide(); }}>
               </div>`
        : nothing}

      <div
        id="panel"
        part="panel"
        popover="manual"
        class="panel ${open ? 'is-open' : ''}"
        data-placement=${this.placement}
        aria-modal=${this.backdrop ? 'true' : 'false'}
      >
        <slot name="content"></slot>
        <slot></slot>
      </div>`;
  }

  static override styles = [styles];
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-overlay': UwcOverlay; }
}
